import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { publicApi as api } from "../lib/api";
import { encryptResponse } from "../lib/crypto";
import { useTranslation } from "react-i18next";

const MAX_LEN = 10000;

export const SecureRequestDeposit = () => {
  const { id } = useParams();
  const { t } = useTranslation("secureRequest");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/secure-requests/${id}`);
      setMeta(data);
    } catch (e) {
      setError(t("deposit.unavailable"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!value.trim()) return;
    setSending(true);
    try {
      const enc = await encryptResponse(meta.public_key_jwk, value);
      await api.post(`/secure-requests/${id}/submit`, enc);
      setValue(""); // efface immédiatement le plaintext du state
      setDone(true);
    } catch (e) {
      toast.error(t("deposit.sendError"));
    } finally {
      setSending(false);
    }
  };

  const wrap = (children) => (
    <div className="min-h-screen app-bg grain flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">{children}</div>
    </div>
  );

  if (loading) return wrap(<div className="glass rounded-2xl p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto" /></div>);

  if (error || !meta) return wrap(
    <div data-testid="sr-deposit-error" className="glass rounded-2xl p-8 text-center">
      <h2 className="font-heading text-xl font-bold mb-2">{t("deposit.unavailableTitle")}</h2>
      <p className="text-sm text-slate-500">{error}</p>
    </div>
  );

  const inactive = meta.status !== "pending";

  if (done) return wrap(
    <motion.div data-testid="sr-deposit-confirm" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4 mx-auto"><Check className="w-7 h-7 text-emerald-600" /></div>
      <h2 className="font-heading text-2xl font-bold mb-2">{t("deposit.sentTitle")}</h2>
      <p className="text-sm text-slate-500">{t("deposit.sentText")}</p>
    </motion.div>
  );

  return wrap(
    <motion.div data-testid="sr-deposit-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 sm:p-8">
      <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600 mb-2">
        <ShieldCheck className="w-4 h-4" /> {t("deposit.eyebrow")}
      </div>
      <h1 data-testid="sr-deposit-title" className="font-heading text-2xl font-extrabold tracking-tight mb-2">{meta.title}</h1>
      {meta.message && <p className="text-sm text-slate-600 leading-relaxed mb-4">{meta.message}</p>}

      {inactive ? (
        <div data-testid="sr-deposit-closed" className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-500">
          {t("deposit.closed")}
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-3">
            {t("deposit.intro")}
          </p>
          <details data-testid="sr-deposit-howto" className="mb-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <summary className="text-xs font-medium text-slate-600 cursor-pointer select-none">
              {t("deposit.howTitle")}
            </summary>
            <p className="text-[11px] text-slate-500 leading-relaxed mt-2">
              {t("deposit.howText")}
            </p>
          </details>
          <Textarea
            data-testid="sr-deposit-input"
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, MAX_LEN))}
            placeholder={t("deposit.placeholder")}
            rows={4}
            className="ph-no-capture bg-white border-slate-200 font-mono-token"
          />
          <Button data-testid="sr-deposit-send" onClick={send} disabled={!value.trim() || sending} className="w-full mt-4 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            {t("deposit.send")}
          </Button>
        </>
      )}
    </motion.div>
  );
};
